import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type {
  PlatformMessage,
  PlatformMessageAttachment,
} from '@liks/backend-svc-api/esm/models/index';
import type { PlatformChatSessionListItemWithIdentity } from '../../types/chatSession';
import { chatQueryKeys } from './chatQueryKeys';

type PlatformChatMessagesPage = Array<PlatformMessage & { id: string }>;
type PlatformChatMessagesInfiniteData = InfiniteData<
  PlatformChatMessagesPage,
  number
>;
type PlatformChatSessionsInfiniteData = InfiniteData<
  PlatformChatSessionListItemWithIdentity[],
  number
>;
type PlatformChatDraftAttachments = Array<
  PlatformMessageAttachment & { id: string }
>;

function bumpSessionWithMessage(
  current: PlatformChatSessionsInfiniteData,
  sessionId: string,
  mostRecentMessage: PlatformMessage,
): { next: PlatformChatSessionsInfiniteData; found: boolean } {
  let found = false;
  let bumped: PlatformChatSessionListItemWithIdentity | null = null;

  const pagesWithoutSession = current.pages.map((page) =>
    page.filter((item) => {
      if (item.session.id !== sessionId) {
        return true;
      }
      found = true;
      bumped = { ...item, mostRecentMessage };
      return false;
    }),
  );

  if (!found || bumped == null) {
    return { next: current, found: false };
  }

  const firstPage = pagesWithoutSession[0] ?? [];
  const pages = [[bumped, ...firstPage], ...pagesWithoutSession.slice(1)];

  return {
    next: {
      ...current,
      pages,
    },
    found: true,
  };
}

/**
 * Updates mostRecentMessage for a session in pinned/unpinned infinite caches
 * and moves it to the front of the first page. Returns whether any cache hit.
 */
export function patchPlatformChatSessionPreview(
  queryClient: QueryClient,
  sessionId: string,
  mostRecentMessage: PlatformMessage,
): boolean {
  let foundInAny = false;

  queryClient.setQueriesData<PlatformChatSessionsInfiniteData>(
    { queryKey: chatQueryKeys.sessions() },
    (current) => {
      if (!current || !Array.isArray(current.pages)) {
        return current;
      }

      const { next, found } = bumpSessionWithMessage(
        current,
        sessionId,
        mostRecentMessage,
      );
      if (found) {
        foundInAny = true;
      }
      return found ? next : current;
    },
  );

  return foundInAny;
}

export function seedPlatformChatMessageAttachmentsFromDrafts(
  queryClient: QueryClient,
  sessionId: string,
  messageId: string,
): void {
  const drafts = queryClient.getQueryData<PlatformChatDraftAttachments>(
    chatQueryKeys.sessionDraftAttachments(sessionId),
  );
  if (!drafts || drafts.length === 0) {
    return;
  }

  queryClient.setQueryData(
    chatQueryKeys.messageAttachments(sessionId, messageId),
    drafts,
  );
}

export function mergePlatformChatMessagesIntoCache(
  queryClient: QueryClient,
  sessionId: string,
  incomingMessages: Array<PlatformMessage & { id: string }>,
): boolean {
  if (incomingMessages.length === 0) {
    return false;
  }

  const queryKey = chatQueryKeys.messages(sessionId);
  const current =
    queryClient.getQueryData<PlatformChatMessagesInfiniteData>(queryKey);

  if (!current || current.pages.length === 0) {
    queryClient.setQueryData<PlatformChatMessagesInfiniteData>(queryKey, {
      pages: [incomingMessages],
      pageParams: [0],
    });
    return true;
  }

  const incomingById = new Map(
    incomingMessages.map((message) => [message.id, message]),
  );
  const existingIds = new Set(
    current.pages.flatMap((page) => page.map((message) => message.id)),
  );
  const newMessages = incomingMessages.filter(
    (message) => !existingIds.has(message.id),
  );

  queryClient.setQueryData<PlatformChatMessagesInfiniteData>(queryKey, {
    ...current,
    pages: current.pages.map((page, index) => {
      const replaced = page.map(
        (message) => incomingById.get(message.id) ?? message,
      );
      if (index === 0 && newMessages.length > 0) {
        return [...replaced, ...newMessages];
      }
      return replaced;
    }),
  });

  return true;
}
